import {copilotLocalStorage} from '@github-ui/copilot-chat-helpers/copilot-local-storage'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useCallback, useEffect, useMemo, useState, type Dispatch, type RefObject, type SetStateAction} from 'react'

import {usePersistedDraft} from '../shared/use-persisted-draft'

export interface SharedSessionDraft {
  currentPrompt: string
  setCurrentPrompt: Dispatch<SetStateAction<string>>
  draftThreadId: string
  /**
   * Clears the draft a submit has taken.
   *
   * Destinations call this instead of writing storage themselves, and it keeps
   * text the user typed after a slow submit started.
   */
  clearDraft: (submittedContent: string) => void
}

/**
 * Owns the draft a new session shares between its task and Chat destinations.
 *
 * Both destinations read the draft and both finish submissions that end it, so
 * persistence lives here alone: `usePersistedDraft` writes a non-empty draft and
 * clears an empty one. Splitting those across the destinations is what let a submitted
 * prompt come back, because `setSavedMessage` is debounced while the clearing
 * `setSavedMessageFast` is not, so a write scheduled just before a submit landed
 * after the clear had already run.
 */
export function useSharedSessionDraft({
  draftThreadId,
  initialPrompt,
  textAreaRef,
}: {
  draftThreadId: string
  /** Read once on mount, so a restored draft never fights later edits. */
  initialPrompt: () => string
  textAreaRef: RefObject<HTMLTextAreaElement | null>
}): SharedSessionDraft {
  const [currentPrompt, setCurrentPrompt] = useState(initialPrompt)
  const [flushOnLeave] = useState(() => isFeatureEnabled('copilot_chat_persist_session_drafts'))

  usePersistedDraft(draftThreadId, 'task', currentPrompt, {flushOnLeave})
  useEffect(() => {
    if (!currentPrompt) copilotLocalStorage.clearSavedUserMessageOnError(draftThreadId)
  }, [currentPrompt, draftThreadId])

  const clearDraft = useCallback(
    (submittedContent: string) => {
      // A slow submit must not discard text the user typed after it started.
      if (textAreaRef.current && textAreaRef.current.value !== submittedContent) return
      setCurrentPrompt('')
    },
    [textAreaRef],
  )

  return useMemo(
    () => ({currentPrompt, setCurrentPrompt, draftThreadId, clearDraft}),
    [clearDraft, currentPrompt, draftThreadId],
  )
}
